import _ from 'lodash';
import { throwError } from 'rxjs';
import { concatMap, first, switchMap } from 'rxjs/operators';

import { OfflineDataFetcher, RecordOfflineDataFetcher } from '@skinsoft/data-store';
import { Cachable } from '@skinsoft/data-store/src/offline/offline-data-update-cacher';
import { EntityDataStore } from '@skinsoft/entity-data-store';

export interface Record extends Cachable {
    id: string;
}

export enum RecordLifecycleState {
    IN_PROGRESS = 'inProgress',
    TO_VALIDATED = 'toValidated',
    VALIDATED = 'validated'
}

export class RecordDataStore extends EntityDataStore<Record, RecordLifecycleState>{
    public removeOfflineData(id: string): void {
        this.offlineDataUpdateCacher.deleteBeforePushing(id)
    }

    public static getInstance() {
        if (!RecordDataStore.instance) {
            RecordDataStore.instance = new RecordDataStore();
        }

        return RecordDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$recordDataStore = RecordDataStore.getInstance();
    }

    private static instance: RecordDataStore = null as any;

    constructor() {
        super('recolement', ['recolement']);
    }

    public exists(id: Record['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public async transitionRecord(recordId: string, lifecycleState: RecordLifecycleState): Promise<Record> {
        if (window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(recordId)) {
            return this.proxy
                .fetch(`/recolement`, { id: recordId, lifecycleState }, 'PATCH')
                .pipe(
                    concatMap(() => this.fetchOne(recordId, true)),
                    first(),
                )
                .toPromise();
        } else {
            return this.fetchOne(recordId).pipe(
                first(),
                switchMap(record => {
                    _.set(record, 'workflowSchema.currentState.name', lifecycleState)
                    _.set(record, 'workflowSchema.currentState.availableLifecycleStates', this.getAvailableStatesFromState(lifecycleState))
                    return this.update(recordId, record)
                }),
                switchMap(() => this.fetchOne(recordId, true)
                ),
            ).toPromise()
        }
    }

    public fetchRandomRecord(offlineItem: boolean = false) {
        if (!window.navigator.onLine || offlineItem) {
            return this.offlineDataFetcher.fetchOneFromPredicate(data => true)
        } else {
            return throwError("This method is not meant to be called while having access to the network")
        }
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new RecordOfflineDataFetcher(this.sanitizedIdentifer);
    }

    private getAvailableStatesFromState(lifecycleState: RecordLifecycleState) {
        switch (lifecycleState) {
            case RecordLifecycleState.IN_PROGRESS:
                return [RecordLifecycleState.TO_VALIDATED, RecordLifecycleState.VALIDATED]
            case RecordLifecycleState.TO_VALIDATED:
                return [RecordLifecycleState.IN_PROGRESS, RecordLifecycleState.VALIDATED]
            case RecordLifecycleState.VALIDATED:
                return [RecordLifecycleState.IN_PROGRESS]
        }

    }
}